<template>
	<div class="analytics">
		<div class="analytics__header">
			<h1 class="analytics__title">Аналитика системы</h1>
			<div class="analytics__period">
				<select v-model="selectedPeriod" class="analytics__select" @change="loadAnalytics">
					<option value="week">Неделя</option>
					<option value="month">Месяц</option>
					<option value="quarter">Квартал</option>
					<option value="year">Год</option>
				</select>
			</div>
		</div>

		<!-- Overview Stats -->
		<div class="analytics__stats">
			<div class="stat-card stat-card--users">
				<div class="stat-card__icon">👥</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.totalUsers || 0 }}</h3>
					<p class="stat-card__label">Всего пользователей</p>
					<span class="stat-card__change stat-card__change--positive">
						+{{ stats.newUsers || 0 }} за период
					</span>
				</div>
			</div>

			<div class="stat-card stat-card--courses">
				<div class="stat-card__icon">📚</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.totalCourses || 0 }}</h3>
					<p class="stat-card__label">Всего курсов</p>
					<span class="stat-card__change stat-card__change--positive">
						+{{ stats.newCourses || 0 }} за период
					</span>
				</div>
			</div>

			<div class="stat-card stat-card--enrollments">
				<div class="stat-card__icon">✅</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.totalEnrollments || 0 }}</h3>
					<p class="stat-card__label">Активных записей</p>
					<span class="stat-card__change stat-card__change--positive">
						+{{ stats.newEnrollments || 0 }} за период
					</span>
				</div>
			</div>

			<div class="stat-card stat-card--revenue">
				<div class="stat-card__icon">💰</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ formatCurrency(stats.revenue) }}</h3>
					<p class="stat-card__label">Доход</p>
					<span class="stat-card__change stat-card__change--positive">
						+{{ stats.revenueGrowth || 0 }}%
					</span>
				</div>
			</div>
		</div>

		<!-- Charts -->
		<div class="analytics__charts">
			<!-- User Growth Chart -->
			<div class="chart-card chart-card--large">
				<h3 class="chart-card__title">Рост пользователей</h3>
				<div class="chart-card__content">
					<Line :data="userGrowthData" :options="chartOptions" />
				</div>
			</div>

			<!-- Course Categories Chart -->
			<div class="chart-card">
				<h3 class="chart-card__title">Курсы по категориям</h3>
				<div class="chart-card__content">
					<Doughnut :data="categoriesData" :options="doughnutOptions" />
				</div>
			</div>

			<!-- Enrollment Trends Chart -->
			<div class="chart-card">
				<h3 class="chart-card__title">Тренд записей</h3>
				<div class="chart-card__content">
					<Bar :data="enrollmentTrendsData" :options="barOptions" />
				</div>
			</div>
		</div>

		<!-- Top Courses -->
		<div class="analytics__section">
			<h2 class="analytics__section-title">Топ курсов</h2>
			<div v-if="loading" class="analytics__loading">
				<div class="spinner"></div>
				<p>Загрузка данных...</p>
			</div>
			<table v-else class="table">
				<thead>
					<tr>
						<th>#</th>
						<th>Курс</th>
						<th>Категория</th>
						<th>Студентов</th>
						<th>Завершено</th>
						<th>Рейтинг</th>
						<th>Доход</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(course, index) in topCourses" :key="course.id">
						<td>{{ index + 1 }}</td>
						<td>
							<strong>{{ course.title }}</strong>
						</td>
						<td>{{ course.category }}</td>
						<td>{{ course.students_count }}</td>
						<td>{{ course.completed_count }}</td>
						<td>
							<div class="table__rating">⭐ {{ course.rating }}</div>
						</td>
						<td>{{ formatCurrency(course.revenue) }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import { Line, Doughnut, Bar } from 'vue-chartjs'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	ArcElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	ArcElement,
	Title,
	Tooltip,
	Legend
)

export default {
	name: 'Analytics',
	components: {
		Line,
		Doughnut,
		Bar,
	},
	data() {
		return {
			loading: false,
			selectedPeriod: 'month',
			stats: {
				totalUsers: 0,
				newUsers: 0,
				totalCourses: 0,
				newCourses: 0,
				totalEnrollments: 0,
				newEnrollments: 0,
				revenue: 0,
				revenueGrowth: 0,
			},
			topCourses: [],
			userGrowthData: {
				labels: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'],
				datasets: [
					{
						label: 'Пользователи',
						data: [120, 190, 230, 280, 340, 420],
						borderColor: '#0095ec',
						backgroundColor: 'rgba(0, 149, 236, 0.1)',
						tension: 0.4,
						fill: true,
					},
				],
			},
			categoriesData: {
				labels: ['Программирование', 'Дизайн', 'Бизнес', 'Маркетинг', 'Другое'],
				datasets: [
					{
						data: [35, 25, 20, 15, 5],
						backgroundColor: ['#0095ec', '#28a745', '#ffc107', '#dc3545', '#6c757d'],
					},
				],
			},
			enrollmentTrendsData: {
				labels: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'],
				datasets: [
					{
						label: 'Записи',
						data: [45, 62, 58, 75, 82, 95],
						backgroundColor: '#0095ec',
					},
				],
			},
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: true,
						position: 'bottom',
					},
				},
			},
			doughnutOptions: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: true,
						position: 'bottom',
					},
				},
			},
			barOptions: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false,
					},
				},
			},
		}
	},
	mounted() {
		this.loadAnalytics()
	},
	methods: {
		async loadAnalytics() {
			this.loading = true
			try {
				// TODO: Implement API calls
				// Mock data for now
				this.stats = {
					totalUsers: 1250,
					newUsers: 85,
					totalCourses: 45,
					newCourses: 3,
					totalEnrollments: 3420,
					newEnrollments: 245,
					revenue: 125000,
					revenueGrowth: 12.5,
				}

				this.topCourses = [
					{
						id: 1,
						title: 'Основы веб-разработки',
						category: 'Программирование',
						students_count: 450,
						completed_count: 320,
						rating: 4.8,
						revenue: 45000,
					},
					{
						id: 2,
						title: 'JavaScript для начинающих',
						category: 'Программирование',
						students_count: 380,
						completed_count: 250,
						rating: 4.7,
						revenue: 38000,
					},
					{
						id: 3,
						title: 'Дизайн интерфейсов',
						category: 'Дизайн',
						students_count: 290,
						completed_count: 180,
						rating: 4.9,
						revenue: 29000,
					},
				]
			} catch (error) {
				console.error('Ошибка загрузки аналитики:', error)
			} finally {
				this.loading = false
			}
		},

		formatCurrency(value) {
			if (!value) return '0 ₽'
			return new Intl.NumberFormat('ru-RU', {
				style: 'currency',
				currency: 'RUB',
				minimumFractionDigits: 0,
			}).format(value)
		},
	},
}
</script>

<style scoped>
.analytics {
	padding: 32px;
	max-width: 1600px;
	margin: 0 auto;
}

.analytics__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 32px;
}

.analytics__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0;
}

.analytics__select {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	cursor: pointer;
}

.analytics__stats {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 24px;
	margin-bottom: 32px;
}

.stat-card {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	display: flex;
	align-items: center;
	gap: 20px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.stat-card:hover {
	transform: translateY(-4px);
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.stat-card__icon {
	font-size: 48px;
}

.stat-card__content {
	flex: 1;
}

.stat-card__value {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0 0 4px 0;
}

.stat-card__label {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	margin: 0 0 8px 0;
}

.stat-card__change {
	display: inline-block;
	padding: 4px 8px;
	border-radius: 6px;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	font-weight: 500;
}

.stat-card__change--positive {
	background-color: #d4edda;
	color: #155724;
}

.analytics__charts {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 24px;
	margin-bottom: 32px;
}

.chart-card {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-card--large {
	grid-column: 1 / -1;
}

.chart-card__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 20px 0;
}

.chart-card__content {
	height: 300px;
}

.analytics__section {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.analytics__section-title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 600;
	margin: 0 0 20px 0;
}

.analytics__loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #0095ec;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin-bottom: 16px;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.table {
	width: 100%;
	border-collapse: collapse;
}

.table th {
	text-align: left;
	padding: 12px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	border-bottom: 2px solid #e8e8e8;
}

.table td {
	padding: 16px 12px;
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	border-bottom: 1px solid #e8e8e8;
}

.table__rating {
	color: #ffc107;
	font-weight: 600;
}
</style>

